import { useMemo } from 'react';
import type { AnyFieldDef } from '@resource-kit/model';
import { useUrlOwnedList, type ListController } from './listController';
import type { ListCapabilities } from '../list/listCapabilities';
import type { ResourceBundle } from './bundle';

// `ListController` / `ListPagination` are NOT re-exported here any more. Both
// entries of this package publish with `export *`, and `react.ts` fans in this
// module and `./listController` side by side, so re-exporting a name that
// module already exports is a duplicate export (TS2308). Consumers get both
// names from `@resource-kit/controllers/react`, which fans in
// `./listController`; #2170 deleted the host shim that used to serve them.

/**
 * Backwards-compatible alias for the controller shape (#1801 named it
 * `RemoteList`; #1803 made it the ONE contract all three sources satisfy).
 */
export type RemoteList<T> = ListController<T>;

export interface RemoteListOptions {
  /** Per-capability opt-in; anything omitted takes the source default (§6.6). */
  capabilities?: ListCapabilities;
  /** Fields in-memory free-text search may match (bulk fetch strategies). */
  searchFields?: readonly string[];
}

/**
 * The URL-owned, server-paginated list source (#1801, #1803).
 *
 * Query state lives in the URL, so the operator's view survives refresh, share
 * links and back/forward — and `search` defaults to **false** unless the
 * resource declares a backend search whitelist, because the list kernel 400s an
 * unsupported `search` param.
 *
 * A definition declaring `pagination: 'client'` IS the `bulk` source in the
 * pre-#1803 schema vocabulary — one capped fetch, everything in memory — so the
 * returned controller reports `source: 'bulk'` for it rather than pretending
 * the rows came back a page at a time. State ownership is identical either way.
 */
export function useRemoteList<T, F extends Record<string, AnyFieldDef>>(
  bundle: ResourceBundle<T, F>,
  options?: RemoteListOptions,
): ListController<T> {
  const source = bundle.host.pagination === 'client' ? ('bulk' as const) : ('remote' as const);
  const searchable = bundle.list.capabilities?.search;
  const listOptions = useMemo(
    () => ({
      source,
      // #1642 / #1803 risk 1. `?? false` is load-bearing and must never
      // become `?? true`: the list kernel answers 400 for a `search`
      // param on a route that declares no searchable columns, so one
      // flipped default 400s production lists en masse. A list view that
      // never declared `capabilities.search` has opted OUT — which is why
      // this is stated here rather than left to the source default
      // (in-memory sources default search ON).
      capabilities: { search: searchable ?? false, ...options?.capabilities },
      searchFields: options?.searchFields,
    }),
    [source, searchable, options?.capabilities, options?.searchFields],
  );
  return useUrlOwnedList(bundle, listOptions);
}
